/** Use-case pages (`/cas-usage/*`), imported into the English dictionary. */
export const useCasesEn = {
	index: {
		hero: {
			label: "Use cases · Maintenance · QHSE · Cleaning · Mobile apps",
			title: "Software designed for one specific trade.",
			emphasis: "one specific trade",
			intro:
				"HVAC maintenance, QHSE consultancies, commercial cleaning, mobile apps left behind by a vendor: for each situation, a typical day, what the trade requires and the tool Lezo would build.",
		},
		list: {
			label: "Four situations",
			title: "Pick the situation closest to yours",
			description:
				"Each page starts from a real working day, the trade’s obligations and the tools on the market, then walks through the tool step by step.",
		},
		audienceLabel: "For",
		link: "See the use case",
		note: {
			title: "Typical scenarios, not client references",
			description:
				"Each use case describes a trade as we studied it: obligations, typical day, tools on the market. The screens are illustrations with fictional data. Your actual process remains the starting point of the scoping.",
		},
		other: {
			title: "Your trade isn’t on the list?",
			description:
				"The method is the same: start from your team’s day and from what your clients expect. Tell us how you work, and we’ll tell you whether custom software makes sense.",
		},
		cta: {
			title: "Which of these situations looks like yours?",
			description:
				"Describe your trade and how your team works today. We’ll get back to you to discuss it.",
			button: "Describe my situation",
		},
	},
	common: {
		frictionLabel: "Friction point",
		referenceLabel: "Reference",
		related: {
			label: "Other use cases",
			title: "Other trades, the same method",
			link: "See the use case",
			all: "All use cases",
		},
	},
	items: {
		hvac: {
			card: {
				name: "HVAC maintenance",
				navDescription:
					"Work orders, maintenance certificates and a property manager portal.",
				title: "From work order to maintenance certificate, without re-entry",
				summary:
					"For heating, ventilation and air-conditioning maintenance companies running service contracts with property managers, social landlords and local authorities.",
				audience: "Owners and operations managers, 10 to 40 technicians",
			},
			hero: {
				label: "Use case · HVAC maintenance · Heating · Air conditioning",
				title: "HVAC maintenance software built around your service contracts.",
				emphasis: "your service contracts",
				intro:
					"Visit planning, boiler-room readings, maintenance certificates, refrigerant records, reports for property managers: Lezo brings your interventions into one app built for you, at a cost that doesn’t depend on how many technicians you have.",
				primaryCta: "Describe my organisation",
				secondaryCta: "See the tool step by step",
			},
			facts: {
				who: {
					label: "For",
					value:
						"HVAC and multi-trade maintenance companies, 10 to 40 technicians",
				},
				tool: {
					label: "What we equip",
					value:
						"Planning, field readings, certificates, refrigerant records, client portal",
				},
				first: {
					label: "First version",
					value:
						"Mobile readings and a generated certificate, for one contract type",
				},
			},
			day: {
				label: "A typical day",
				title: "A maintenance day, today",
				description:
					"The scenario we see most often in companies with 10 to 40 technicians. Every step works, but the information changes medium three times before it reaches the client.",
				moments: {
					a: {
						time: "07:30",
						actor: "Technician",
						text: "Karim picks up today’s work orders: three preventive visits, plus a breakdown added by text message the evening before.",
						friction:
							"The schedule lives in a spreadsheet only the coordinator edits.",
					},
					b: {
						time: "09:15",
						actor: "Technician · boiler room",
						text: "In a residential boiler room, he records pressure, temperatures and the combustion analysis on a paper form.",
						friction:
							"No signal underground: photos stay on his personal phone.",
					},
					c: {
						time: "11:40",
						actor: "Technician · rooftop",
						text: "Leak check on a rooftop unit: refrigerant charge and result go on a regulatory form filled in by hand.",
						friction: "The next check date is kept in yet another spreadsheet.",
					},
					d: {
						time: "17:30",
						actor: "Operations coordinator",
						text: "Julie re-types the day’s work orders into the invoicing software and chases two technicians for missing forms.",
						friction:
							"One to two hours of re-entry a day, and invoicing waits.",
					},
					e: {
						time: "End of season",
						actor: "Management",
						text: "A property manager asks for the annual report on their twelve buildings, with maintenance certificates and visit dates.",
						friction:
							"The season must be rebuilt from PDFs, photos and emails.",
					},
				},
			},
			stakes: {
				label: "What the trade requires",
				title: "Obligations that produce paperwork",
				description:
					"Every visit must leave a precise record, within a set deadline. That’s where time is lost, and where a tool built for the job makes the difference.",
				items: {
					attestation: {
						title: "The maintenance certificate within 15 days",
						description:
							"In France, every boiler from 4 to 400 kW is serviced once a year. The certificate, with measured efficiency and emissions, is handed over within 15 days of the visit.",
						ref: "Decree 2009-649 · order of 15 September 2009",
					},
					fluids: {
						title: "Refrigerant tracking",
						description:
							"Every intervention on refrigeration equipment requires an intervention record, and leak-check frequency depends on the charge in tonnes of CO2 equivalent.",
						ref: "Regulation (EU) 2024/573 · Cerfa form 15497",
					},
					schedule: {
						title: "A different deadline for each piece of equipment",
						description:
							"Yearly servicing for boilers, every two years for heat pumps and air conditioning, an inspection every five years above 70 kW: each piece of equipment has its own calendar.",
						ref: "Decrees 2009-649 and 2020-912",
					},
				},
			},
			build: {
				label: "What Lezo builds",
				title: "One tool, from planning to the client report",
				description:
					"An example application for a maintenance company. Yours starts from your contracts, your reading forms and your vocabulary.",
				steps: {
					a: {
						title: "The schedule builds itself from the contracts",
						description:
							"Visits required by each contract appear in the schedule on their own. The coordinator only adds breakdowns and assigns technicians.",
					},
					b: {
						title: "Readings are taken in the boiler room, even offline",
						description:
							"The technician enters measurements in a form that mirrors your own work order. Photos and the caretaker’s signature are attached, then everything syncs on the way out.",
					},
					c: {
						title: "The certificate goes out the same day",
						description:
							"The readings feed the maintenance certificate and the refrigerant record. The signed document goes to the client without re-entry, well before the 15-day deadline.",
					},
					d: {
						title:
							"Property managers follow their buildings without calling you",
						description:
							"In their space, managers see completed visits, download certificates and find the season report for each of their buildings.",
					},
				},
			},
			why: {
				label: "Why custom",
				title: "Why a tool built for your company",
				description:
					"Field-service software is paid per technician per month and imposes its own work-order template. A tool of your own follows your contracts and doesn’t cost more as the team grows.",
				items: {
					forms: {
						title: "Your forms, not a vendor’s",
						description:
							"Your readings, your service levels, your wording: the tool takes over your current documents instead of imposing its own.",
					},
					proof: {
						title: "Proof of visit that holds up",
						description:
							"Time, photos and signature on every intervention: what you need to answer a dispute over a deadline or a penalty.",
					},
					growth: {
						title: "Hire without paying for licences",
						description:
							"The cost doesn’t depend on the number of technicians. Hiring for the heating season doesn’t change the bill.",
					},
					offline: {
						title: "Designed for boiler rooms",
						description:
							"Data entry works offline and syncs as soon as the phone finds a connection again.",
					},
				},
				visual: {
					title: "Field-service software or your own tool, over six years",
					subscription: "Field-service software subscription",
					note: "Indicative assumption: 25 users at €50 a month, within the range of field-service software on the market.",
				},
			},
			scope: {
				first: {
					label: "First version",
					title: "What a first version would include",
					items: {
						a: "The reading form for your main visit type, usable offline.",
						b: "The maintenance certificate generated from the readings, on your letterhead.",
						c: "The schedule of visits derived from service contracts.",
						d: "Your installed base imported: sites, equipment and current contracts.",
					},
				},
				check: {
					label: "At scoping",
					title: "What we check together",
					items: {
						a: "Visit types, their forms and their actual frequency.",
						b: "The invoicing or maintenance software the tool exchanges data with.",
						c: "The documents your clients expect: certificates, reports, refrigerant records.",
						d: "The client portal: real demand from your property managers, or a later step.",
					},
				},
			},
			faq: {
				title: "Your questions about custom maintenance software",
				items: {
					saas: {
						question: "Why not use field-service software from the market?",
						answer:
							"Sometimes that’s the right answer, and scoping compares the options. Custom software makes sense when your forms and contracts don’t fit the vendor’s model, or when the per-technician cost gets high: 25 users at €50 a month come to €15,000 a year.",
					},
					offline: {
						question: "Does the app work in a boiler room, without signal?",
						answer:
							"Yes, it’s a requirement from day one. Readings, photos and signatures are stored on the phone and sent automatically as soon as the connection is back.",
					},
					adoption: {
						question: "Will my technicians actually use it?",
						answer:
							"The mobile form mirrors the work order they know, with fewer fields to fill in. Two or three technicians test it on real visits before rollout, and their feedback is built in.",
					},
					documents: {
						question: "Are the generated certificates compliant?",
						answer:
							"The template includes the items required by the order of 15 September 2009 and your own layout. It’s validated with you before going live; measurements remain the responsibility of the technician who signs.",
					},
					data: {
						question: "What happens to our existing contracts and equipment?",
						answer:
							"Sites, equipment and contracts are imported from your current files when the tool goes live. Scoping specifies what is imported and how.",
					},
				},
			},
			cta: {
				title: "How are your visits organised today?",
				description:
					"How many technicians, which contracts, which documents do your clients expect? A few lines are enough to prepare the conversation.",
				button: "Describe my organisation",
			},
			scene: {
				labels: {
					planning: "Planning",
					survey: "Readings",
					certificate: "Certificate",
					portal: "Manager portal",
				},
				host: "maintenance.your-company.com",
				planning: {
					title: "Planning · week 42",
					fromContract: "Contract",
					urgent: "Breakdown",
					days: {
						mon: "Mon",
						tue: "Tue",
						wed: "Wed",
						thu: "Thu",
						fri: "Fri",
					},
					visits: {
						a: { site: "The Limes", type: "Annual service", tech: "KB" },
						b: { site: "Quay 12", type: "Rooftop leak check", tech: "JR" },
						c: { site: "Wisteria Home", type: "Hot water failure", tech: "KB" },
						d: { site: "Lurçat School", type: "Season start-up", tech: "MD" },
						e: { site: "The Park", type: "Annual service", tech: "JR" },
					},
				},
				survey: {
					title: "WO 2026-1147",
					place: "Gas boiler room · The Limes",
					offline: "Offline",
					synced: "Synced",
					sign: "Caretaker’s signature",
					fields: {
						co: { label: "Ambient CO", value: "2 ppm" },
						co2: { label: "CO2", value: "9.1%" },
						flue: { label: "Flue gas", value: "54 °C" },
						efficiency: { label: "Efficiency", value: "98.6%" },
					},
					checks: {
						burner: "Burner cleaned",
						vessel: "Expansion vessel at 1.5 bar",
					},
				},
				certificate: {
					title: "Maintenance certificate",
					client: "The Limes residence · Morvan Property",
					checksTitle: "Items checked",
					boiler: "Gas condensing boiler · 225 kW",
					signed: "Signed by K. B.",
					sent: "Sent · D+0",
				},
				portal: {
					title: "Morvan Property · Your buildings",
					season: "Heating season 2026–2027",
					progress: "Visits completed",
					progressValue: "38 / 42",
					download: "Certificate",
					rows: {
						a: { site: "The Limes residence", status: "Done · 14 Oct" },
						b: { site: "The Park residence", status: "Done · 13 Oct" },
						c: { site: "Anjou Gardens", status: "Planned · 21 Oct" },
					},
				},
			},
		},
		qhse: {
			card: {
				name: "QHSE consultancy",
				navDescription:
					"Audits, action plans and a follow-up portal for your clients.",
				title:
					"Audits, action plans and a client portal for QHSE consultancies",
				summary:
					"For quality, health, safety and environment consultancies that run audits, risk assessments and certification support.",
				audience: "Heads of consultancies with 3 to 15 consultants",
			},
			hero: {
				label:
					"Use case · QHSE consulting · Risk assessment · ISO 9001, 14001, 45001",
				title:
					"The audit and action-plan follow-up tool for your QHSE consultancy.",
				emphasis: "action-plan follow-up",
				intro:
					"Findings captured on site, rating with your method, a report in your branding, an action plan your clients update themselves: Lezo builds your consultancy’s tool, and the client portal becomes a service you charge for.",
				primaryCta: "Describe my method",
				secondaryCta: "See the tool step by step",
			},
			facts: {
				who: {
					label: "For",
					value: "QHSE and prevention consultancies, 3 to 15 consultants",
				},
				tool: {
					label: "What we equip",
					value:
						"Audit checklists, rating, reports, action plans, client portal",
				},
				first: {
					label: "First version",
					value:
						"Audit checklist on a tablet and a report generated in your branding",
				},
			},
			day: {
				label: "A typical engagement",
				title: "An audit engagement, today",
				description:
					"The usual course of a safety audit or an internal ISO audit at a client SME. The real work happens on site; what comes after is what costs.",
				moments: {
					a: {
						time: "08:30",
						actor: "Consultant · on site",
						text: "Tour of the workshop and the shipping dock: photos on the phone, findings noted on a printed checklist.",
						friction:
							"A different Excel checklist for every client and every standard.",
					},
					b: {
						time: "14:00",
						actor: "Consultant · debrief",
						text: "On-the-spot debrief with the CEO and the QHSE manager: major gaps are announced, owners appointed verbally.",
						friction:
							"Commitments made in the meeting are written down nowhere.",
					},
					c: {
						time: "Evening",
						actor: "Consultant · office",
						text: "Writing the Word report: copying findings, inserting photos, redoing the rating, formatting the action plan.",
						friction: "One to two days of writing for one day of audit.",
					},
					d: {
						time: "D+15",
						actor: "Client",
						text: "The action plan arrives as an attachment. The QHSE manager forwards it, then it vanishes in the inbox.",
						friction: "Nobody knows which actions are closed.",
					},
					e: {
						time: "D+11 months",
						actor: "Consultancy",
						text: "Before the surveillance audit, everything must be pieced together: reminders, proof received by email, versions of the risk assessment.",
						friction:
							"The consultancy’s value between two engagements stays invisible.",
					},
				},
			},
			stakes: {
				label: "What the trade requires",
				title: "Obligations that don’t end with the report",
				description:
					"Your clients must prove over time what they did with your recommendations. It’s a chance to stay present between two engagements.",
				items: {
					duerp: {
						title: "A risk assessment kept up to date",
						description:
							"In France, the single risk-assessment document is mandatory from the first employee, updated at least yearly from 11 employees, kept for 40 years in all its versions, with a yearly prevention programme from 50 employees.",
						ref: "French Labour Code, art. L4121-3-1 and R4121-2",
					},
					iso: {
						title: "Corrective actions that get checked",
						description:
							"ISO 9001, 14001 and 45001 require internal audits, management review and handling of nonconformities. Every surveillance audit checks the previous year’s actions.",
						ref: "ISO 9001, 14001 and 45001, clauses 9 and 10",
					},
					prevention: {
						title: "Prevention plans to keep alive",
						description:
							"Prevention plans with outside contractors, safety protocols for loading and unloading: more documents to produce and follow up at your clients.",
						ref: "French Labour Code, art. R4512-7 and R4515-4",
					},
				},
			},
			build: {
				label: "What Lezo builds",
				title: "From the site visit to client follow-up, in one tool",
				description:
					"An example tool for a QHSE consultancy. Yours takes over your checklists, your rating method and your report layout.",
				steps: {
					a: {
						title: "Findings are captured during the visit",
						description:
							"On a tablet or phone, the consultant follows the standard’s checklist, takes photos, classifies each finding (major or minor nonconformity, gap, improvement) and links it to a work unit.",
					},
					b: {
						title: "Rating ranks the risks",
						description:
							"Severity and frequency according to your method: every risk lands in the matrix, and priority situations stand out on their own.",
					},
					c: {
						title: "Report and action plan are ready that evening",
						description:
							"The report in your branding is generated from the findings. The action plan lists each gap with its owner, deadline and expected proof.",
					},
					d: {
						title: "The client keeps the action plan alive",
						description:
							"In the portal, owners update their actions and upload their proof. You follow all your clients and prepare the next audit in minutes.",
					},
				},
			},
			why: {
				label: "Why custom",
				title: "Why a tool of your consultancy’s own",
				description:
					"QHSE software on the market is built for the audited company, not for the consultancy supporting several clients. Your tool follows your method and lets you sell follow-up.",
				items: {
					method: {
						title: "Your method, not a vendor’s",
						description:
							"Your checklists per standard, your rating scale, your wording: your know-how stays yours.",
					},
					report: {
						title: "Reports in your branding",
						description:
							"The report comes out in your layout, without copying or formatting.",
					},
					clients: {
						title: "Clients invited without licences",
						description:
							"Executives and action owners use the portal with no per-user cost.",
					},
					revenue: {
						title: "A new revenue line",
						description:
							"Action-plan follow-up becomes a subscription you offer at the end of each engagement.",
					},
				},
				visual: {
					title: "The follow-up portal, paid back within a year",
					revenue: "Cumulative follow-up subscriptions",
					cost: "First version",
					payback: "Covered in month {month}",
					months: "Month",
					summary:
						"Under this assumption, cumulative subscriptions cover the first version ({cost}) in month {month} and reach {total} over twelve months.",
					note: "Indicative assumption: a follow-up subscription at {price} per client per month, with {clients} new subscribing clients each month.",
				},
			},
			scope: {
				first: {
					label: "First version",
					title: "What a first version would include",
					items: {
						a: "Your main audit checklist, on tablet and phone.",
						b: "Your rating method and the criticality matrix.",
						c: "The report generated in your branding, with photos and action plan.",
						d: "Action tracking per client, with automatic reminders.",
					},
				},
				check: {
					label: "At scoping",
					title: "What we check together",
					items: {
						a: "The standards and checklists to take over first.",
						b: "Your rating scale and priority rules.",
						c: "The client portal: your clients’ interest and the price of follow-up.",
						d: "Hosting and confidentiality of your clients’ data.",
					},
				},
			},
			faq: {
				title: "Your questions about a custom audit tool",
				items: {
					excel: {
						question: "Why change, if Excel and Word do the job?",
						answer:
							"They do the job for producing a report. They don’t let you track twenty clients’ actions, nor prove what was done when the next audit comes. The tool takes over your templates: you keep your method and stop copying.",
					},
					portal: {
						question: "Will my clients really use a portal?",
						answer:
							"The portal only asks the action owner to update an action and attach proof, from a link received by email. The first version can start with two or three willing clients before being offered to all.",
					},
					price: {
						question: "Isn’t €20,000 a lot for a consultancy?",
						answer:
							"It’s the order of magnitude of a first version. It pays back through the writing time saved and, if you wish, through the follow-up subscription sold to your clients. Scoping prices the exact scope.",
					},
					data: {
						question: "Where is our clients’ data hosted?",
						answer:
							"In Europe, with a host chosen with you. The consultancy stays in control of the data, and each client only sees their own file.",
					},
					duerp: {
						question: "Can the tool handle the risk-assessment document?",
						answer:
							"It can version every update of the risk assessment and keep its history, as French labour law requires. The exact scope is decided at scoping, depending on your engagements.",
					},
				},
			},
			cta: {
				title: "How do you follow up on your clients’ action plans?",
				description:
					"Your standards, your rating method, the number of clients you follow: a few lines are enough to prepare the conversation.",
				button: "Describe my method",
			},
			scene: {
				labels: {
					findings: "Findings",
					rating: "Rating",
					plan: "Action plan",
					portal: "Client portal",
				},
				host: "audit.your-consultancy.com",
				findings: {
					title: "Audit · Three Rivers Cannery",
					subtitle: "ISO 45001 · internal audit",
					photos: "12 photos",
					items: {
						a: {
							tag: "Major NC",
							ref: "§9.2",
							text: "No internal audit in 14 months",
						},
						b: {
							tag: "Minor NC",
							ref: "§7.2",
							text: "Electrical clearance expired for two operators",
						},
						c: {
							tag: "Gap",
							ref: "Risk",
							text: "No safety protocol at the shipping dock",
						},
						d: {
							tag: "Idea",
							ref: "§9.3",
							text: "Track near misses in management review",
						},
					},
				},
				rating: {
					title: "Criticality matrix",
					gravity: "Severity",
					frequency: "Frequency",
					priority: "Priority from 12",
					risks: {
						a: { name: "Autoclave · steam burn", score: "S4 × F3 = 12" },
						b: { name: "Dock · loading", score: "S3 × F3 = 9" },
						c: { name: "Site, 2nd floor · fall", score: "S4 × F2 = 8" },
					},
				},
				plan: {
					title: "Action plan",
					columns: { action: "Action", owner: "Owner", due: "Due" },
					rows: {
						a: {
							action: "Fit the protective guard",
							owner: "Maintenance",
							due: "15/11",
						},
						b: {
							action: "Schedule the internal audit",
							owner: "C. Martin",
							due: "30/11",
						},
						c: {
							action: "Write the safety protocol",
							owner: "Logistics",
							due: "06/12",
						},
					},
					report: "Audit report · 18 pages",
					generated: "Generated",
				},
				portal: {
					host: "follow-up.your-consultancy.com",
					title: "Three Rivers · action plan",
					stats: { done: "Closed", progress: "In progress", late: "Overdue" },
					action: "Fit the protective guard",
					proof: "Proof uploaded by the owner · photo",
					closed: "Closed",
					nextAudit: "Next surveillance audit · 12/03",
				},
			},
		},
		cleaning: {
			card: {
				name: "Commercial cleaning",
				navDescription:
					"Multi-site quality control and proof of service for clients.",
				title:
					"Multi-site quality control and proof of service for cleaning companies",
				summary:
					"For commercial cleaning companies that must prove the quality of their service to every client, and defend it at contract renewal.",
				audience: "Branch managers, quality managers and area supervisors",
			},
			hero: {
				label: "Use case · Commercial cleaning · Quality control · Multi-site",
				title: "Quality-control software to prove every site is clean.",
				emphasis: "prove every site is clean",
				intro:
					"Checklists matching each client’s specification, a score computed on the spot, nonconformities tracked until they are fixed, a dedicated space for the client: Lezo builds the tool that turns your inspections into a renewal file.",
				primaryCta: "Describe my inspections",
				secondaryCta: "See the tool step by step",
			},
			facts: {
				who: {
					label: "For",
					value:
						"B2B cleaning companies: offices, healthcare, residential, industry",
				},
				tool: {
					label: "What we equip",
					value:
						"Inspection checklists, scores, nonconformities, reports, client portal",
				},
				first: {
					label: "First version",
					value: "Mobile inspection and a monthly report per site",
				},
			},
			day: {
				label: "A typical day",
				title: "An area supervisor’s day, today",
				description:
					"An area supervisor often covers several sites a day, between inspections, replacements and client meetings. Quality is delivered in the field, but it’s proven on paper.",
				moments: {
					a: {
						time: "06:45",
						actor: "Area supervisor",
						text: "Two cleaners absent at an office building: he arranges replacements by phone before the first occupants arrive.",
						friction:
							"A missed round will only reach the client if they notice it.",
					},
					b: {
						time: "09:30",
						actor: "Area supervisor · inspection",
						text: "Inspection of the restrooms and reception with a paper checklist: floors, supplies, touch points, odour.",
						friction:
							"The checklist doesn’t quite match the client’s specification.",
					},
					c: {
						time: "11:00",
						actor: "Area supervisor · clinic",
						text: "At the clinic, dust on a light rail in a patient room: he tells the team leader and heads to the next site.",
						friction:
							"The nonconformity has no owner, no deadline, no proof it was fixed.",
					},
					d: {
						time: "15:00",
						actor: "Client",
						text: "A client’s facilities manager complains about the cafeteria and asks for a summary of the quarter’s inspections.",
						friction: "Inspections sit in binders, across three branches.",
					},
					e: {
						time: "Renewal",
						actor: "Management",
						text: "The tender is reopened. The technical proposal must demonstrate quality over the past years.",
						friction:
							"Without a measured track record, quality is defended with words.",
					},
				},
			},
			stakes: {
				label: "What’s at stake",
				title: "What’s at stake at every renewal",
				description:
					"When a contract changes hands in France, site staff usually move with it. What sets a company apart is its supervision and its ability to prove quality.",
				items: {
					transfer: {
						title: "Staff follow the contract",
						description:
							"When the provider changes, employees assigned to the site are taken over by the incoming company, under conditions. The difference lies in organisation and proof.",
						ref: "French cleaning collective agreement (IDCC 3043), article 7",
					},
					measure: {
						title: "Quality measured, not claimed",
						description:
							"Specifications call for sample-based inspections with quality levels, scoring and corrective actions, often referring to the sector’s standards.",
						ref: "EN 13549 · NF X50-794-1",
					},
					health: {
						title: "Stricter requirements in healthcare",
						description:
							"In healthcare facilities, each zone has its risk level, disinfection protocols and frequencies, validated by the facility.",
						ref: "Risk zones 1 to 4 · NF S90-351",
					},
				},
			},
			build: {
				label: "What Lezo builds",
				title: "From field inspection to client report, in one tool",
				description:
					"An example tool for a multi-site cleaning company. Yours takes over your checklists, your contractual thresholds and your branch organisation.",
				steps: {
					a: {
						title: "One checklist per site: the contract’s",
						description:
							"The supervisor inspects zone by zone with the checklist from the client’s specification: pass, fail, photo when needed, even offline.",
					},
					b: {
						title: "The score is computed on the spot",
						description:
							"As soon as the inspection ends, the site and zone scores appear, compared with the contractual threshold.",
					},
					c: {
						title: "Every nonconformity has an owner",
						description:
							"The nonconformity goes to the team leader with the photo and a deadline. The fix is proven with a new, time-stamped photo.",
					},
					d: {
						title: "The client sees quality, month after month",
						description:
							"In their space, the client sees scores, inspections and corrective actions for their sites. The monthly report goes out without being written.",
					},
				},
			},
			why: {
				label: "Why custom",
				title: "Why a tool built for your contracts",
				description:
					"Time-tracking and form apps impose their own checklist. Your tool takes over each client’s and builds, inspection after inspection, the proof you’ll need at renewal.",
				items: {
					grid: {
						title: "Each client’s checklist",
						description:
							"Zones, criteria, frequencies and thresholds specific to each contract, without compromise.",
					},
					loop: {
						title: "Nonconformities that get closed",
						description:
							"Photo, owner, deadline, proven fix: penalties are discussed on facts.",
					},
					portal: {
						title: "Clients who see, without chasing",
						description:
							"Scores, photos and actions available to the client at any time.",
					},
					field: {
						title: "Designed for the field",
						description:
							"Pictograms, photos and quick input, including offline in basements.",
					},
				},
				visual: {
					title: "The renewal file builds itself",
					series: "Monthly score · Lumen Tower",
					threshold: "Contract threshold {value}",
					months: "N D J F M A M J J A S O",
					average: "12-month average",
					closing: "Average time to fix",
					closingValue: "26 h",
					controls: "Inspections completed",
					controlsValue: "48",
					note: "Illustration with fictional data: twelve months of inspections on one site.",
				},
			},
			scope: {
				first: {
					label: "First version",
					title: "What a first version would include",
					items: {
						a: "The inspection checklist for your main contracts, usable offline.",
						b: "Scores per zone and per site, compared with the contract threshold.",
						c: "Nonconformity tracking until they are fixed.",
						d: "The monthly report per site, ready to send.",
					},
				},
				check: {
					label: "At scoping",
					title: "What we check together",
					items: {
						a: "Checklists and thresholds of your most demanding contracts.",
						b: "How inspections are organised: who inspects, how often, which sites.",
						c: "The client portal: what your clients see, and what stays internal.",
						d: "Existing tools: time tracking, scheduling, payroll.",
					},
				},
			},
			faq: {
				title: "Your questions about a custom quality-control tool",
				items: {
					cheap: {
						question:
							"Inspection apps exist for a few dozen euros a month. Why go custom?",
						answer:
							"For a first need for traceability, they do the job. Custom software makes sense when each client has their own checklist, thresholds and reporting requirements, and when quality must become an argument at renewal. Scoping compares both options.",
					},
					exposure: {
						question: "Won’t a client portal expose our nonconformities?",
						answer:
							"You choose what the client sees. Showing a nonconformity fixed within hours is often more reassuring than a perfect inspection on paper.",
					},
					field: {
						question:
							"Our team leaders aren’t comfortable with digital tools. Is that a problem?",
						answer:
							"The app is designed for them: large buttons, pictograms, photos, very little typing. It’s tested with them on real inspections before rollout.",
					},
					client: {
						question: "Some clients have their own inspection checklist.",
						answer:
							"That’s exactly what the tool handles: each contract can have its own checklist, criteria and threshold, including for joint inspections with the client.",
					},
					network: {
						question: "What about basements with no signal?",
						answer:
							"Inspections are stored on the phone and sent as soon as the connection is back.",
					},
				},
			},
			cta: {
				title: "How do you prove the quality of your service?",
				description:
					"Number of sites, contract types, how you inspect: a few lines are enough to prepare the conversation.",
				button: "Describe my inspections",
			},
			scene: {
				labels: {
					control: "Inspection",
					score: "Score",
					fix: "Nonconformity",
					portal: "Client portal",
				},
				host: "quality.your-company.com",
				control: {
					title: "Inspection · Lumen Tower",
					zone: "Restrooms · level 2",
					photo: "Photo attached to the criterion",
					next: "Next zone",
					criteria: {
						floors: "Floors spotless",
						bins: "Bins emptied",
						contact: "Touch points",
						supplies: "Supplies",
						mirrors: "Mirrors",
					},
				},
				score: {
					title: "Inspection score",
					site: "Lumen Tower · 14 zones",
					threshold: "Threshold 90%",
					zones: {
						reception: "Reception",
						office: "Open space, level 3",
						sanitary: "Restrooms, level 2",
						cafeteria: "Cafeteria",
					},
				},
				fix: {
					title: "Nonconformity · NC-0412",
					text: "Soap dispenser empty",
					zone: "Lumen Tower · restrooms, level 2",
					before: "Finding",
					after: "Fixed",
					owner: "K. Mbaye · team leader",
					due: "Due today, 14:00",
					action: "Refill and add to the 2 pm round",
					lifted: "Fixed in 3 h",
				},
				portal: {
					title: "Nordis Group · your sites",
					period: "Last twelve months",
					threshold: "Threshold 90%",
					report: "October report sent",
					open: "2 open NCs",
					sites: {
						a: "Lumen Tower",
						b: "Nordis Head Office",
						c: "Part-Dieu Branch",
					},
				},
			},
		},
		takeover: {
			card: {
				name: "React Native app takeover",
				navDescription:
					"An orphaned app to bring back in line with the stores.",
				title: "Taking over a React Native app after the vendor has left",
				summary:
					"For companies whose mobile app was delivered by an agency or a freelancer who has since moved on, and that nobody knows how to build or publish anymore.",
				audience: "CEOs and CTOs of SMEs and start-ups without a mobile team",
			},
			hero: {
				label: "Use case · React Native · Expo · App Store and Google Play",
				title: "Take over your React Native app, without rewriting it.",
				emphasis: "without rewriting it",
				intro:
					"Your vendor is gone, the build no longer passes and the stores require an update? Lezo recovers your accounts, assesses the app, brings it in line on Expo and hands you back a release pipeline you control.",
				primaryCta: "Review my app",
				secondaryCta: "See the takeover step by step",
			},
			facts: {
				who: {
					label: "For",
					value:
						"SMEs and start-ups whose app was delivered by a vendor who left",
				},
				tool: {
					label: "What we take over",
					value: "Accounts, assessment, Expo migration, builds and releases",
				},
				first: {
					label: "First step",
					value: "A written assessment, with a date and a price",
				},
			},
			day: {
				label: "A common scenario",
				title: "How an app becomes orphaned",
				description:
					"Nothing breaks overnight: each month takes the app a little further from what the stores accept, until the day an update becomes mandatory.",
				moments: {
					a: {
						time: "Delivery",
						actor: "Agency",
						text: "The app is delivered and published. Developer accounts, certificates and the signing key stay with the agency, “to keep things simple”.",
						friction: "Nothing is in your name, and nothing is documented.",
					},
					b: {
						time: "A year later",
						actor: "Freelancer",
						text: "The developer who knew the project moves on. Small fixes used to ship as over-the-air updates.",
						friction:
							"The update service used, CodePush, shut down in March 2025.",
					},
					c: {
						time: "Late summer",
						actor: "Google Play",
						text: "The Play Console warns that the app must target a newer API level before the deadline.",
						friction:
							"After the deadline, new users on recent devices can no longer find it.",
					},
					d: {
						time: "The next day",
						actor: "CEO",
						text: "A new developer tries to run the build: Xcode version rejected, abandoned dependencies, keystore nowhere to be found.",
						friction: "Nobody knows how to build the app anymore.",
					},
					e: {
						time: "A week later",
						actor: "Agency",
						text: "The quote arrives: rewrite everything, several months of work and a five-figure budget.",
						friction: "Yet the business code still works.",
					},
				},
			},
			stakes: {
				label: "What the stores require",
				title: "Requirements that come back every year",
				description:
					"A mobile app can’t stand still: Google and Apple regularly raise their technical requirements, on precise dates.",
				items: {
					android: {
						title: "Google Play raises the API level every year",
						description:
							"Every late August, Google Play requires a newer API level for updates: Android 16 (API 36) since 31 August 2026. Beyond that, the app is no longer offered to new users on recent devices.",
						ref: "Google Play · target API level requirements",
					},
					ios: {
						title: "The App Store requires a recent Xcode",
						description:
							"To publish an update, Apple requires a recent version of Xcode and the iOS SDK: Xcode 26 and the iOS 26 SDK since 28 April 2026. An old project must first be brought up to date.",
						ref: "Apple Developer · submission requirements",
					},
					ownership: {
						title: "Accounts and keys in your name",
						description:
							"The developer account, the Android signing key and Apple certificates govern every update. Apple and Google both allow an app to be transferred without losing users or reviews.",
						ref: "Play App Signing · app transfer",
					},
				},
			},
			build: {
				label: "What Lezo does",
				title: "The takeover, step by step",
				description:
					"The method of an Expo migration, preceded by an often forgotten step: recovering what belongs to you.",
				steps: {
					a: {
						title: "Recover your accounts",
						description:
							"Code repository, App Store Connect and Play Console accounts, signing key, certificates, Firebase, environment variables: we take inventory and transfer everything to your name.",
					},
					b: {
						title: "Assess before quoting",
						description:
							"Every native dependency is reviewed: equivalent Expo module, plugin to write or native code kept. You receive a written assessment, a date and a price.",
					},
					c: {
						title: "Bring the app in line",
						description:
							"The app moves to Expo and React Native’s New Architecture, targets the required API levels and SDKs, and over-the-air updates resume with EAS Update.",
					},
					d: {
						title: "Release, then hand over",
						description:
							"Builds and store submission are automated. Your team or a new vendor takes over with the documentation, or Lezo handles maintenance.",
					},
				},
			},
			why: {
				label: "Take over or rewrite",
				title: "Taking over costs less than starting again",
				description:
					"Your app’s screens, business logic and data still work. What has aged is the tooling around them: that’s what we replace.",
				items: {
					keep: {
						title: "The app code is kept",
						description:
							"Screens, business logic and data stay in place: your users update the app as usual.",
					},
					own: {
						title: "You own it again",
						description:
							"Accounts, keys, certificates and repository in your name, documented: your app no longer depends on a vendor.",
					},
					date: {
						title: "A date and a price first",
						description:
							"The assessment sets the scope before any commitment, taking store deadlines into account.",
					},
					later: {
						title: "Lighter maintenance afterwards",
						description:
							"On Expo, upgrades and releases become routine tasks again.",
					},
				},
			},
			scope: {
				first: {
					label: "Assessment",
					title: "What the assessment contains",
					items: {
						a: "An inventory of accounts, and the plan to transfer them to your name.",
						b: "The state of the project: versions, iOS and Android builds, native dependencies.",
						c: "The store deadlines that apply to your app.",
						d: "The takeover plan, its timeline and its price.",
					},
				},
				check: {
					label: "To get started",
					title: "What we need",
					items: {
						a: "Read access to the code repository, or an archive of the project.",
						b: "Developer account credentials, or the contact of their holder.",
						c: "The list of services used: backend, notifications, payments, analytics.",
						d: "Someone who knows how the app is used.",
					},
				},
			},
			faq: {
				title: "Your questions about taking over an app",
				items: {
					rewrite: {
						question: "Does the app need to be rewritten?",
						answer:
							"Rarely. Most of the app code is kept: native configuration, some dependencies and the build pipeline are what change. The assessment identifies the parts that need more work.",
					},
					keystore: {
						question: "We lost the Android signing key. Is that serious?",
						answer:
							"If the app uses Play App Signing, Google can reset the upload key and updates remain possible. Otherwise it’s trickier: it’s the first thing the assessment checks.",
					},
					accounts: {
						question: "The accounts are in the former agency’s name. What now?",
						answer:
							"Apple and Google both let you transfer an app to your own account without losing users or reviews. We prepare the request and guide you through it.",
					},
					deadline: {
						question:
							"The Google Play deadline has passed or is close. Is there time?",
						answer:
							"Google lets you request an extension, until 1 November for the August deadline. The assessment starts with whatever blocks publishing, to ship a compliant update as soon as possible.",
					},
					users: {
						question: "Will users lose their data?",
						answer:
							"No. The app keeps its identifier and signature: it’s a normal update, installed over the existing version.",
					},
				},
			},
			cta: {
				title: "Where does your app stand?",
				description:
					"Who built it, when was it last published, what’s blocking? Those three answers are enough for a first conversation.",
				button: "Review my app",
			},
			scene: {
				labels: {
					access: "Accounts",
					diagnosis: "Assessment",
					compliance: "Compliance",
					release: "Release",
				},
				access: {
					file: "accounts-inventory.md",
					title: "Accounts inventory",
					owner: "Everything is now in your name",
					items: {
						repo: { name: "Git repository", status: "Transferred" },
						apple: { name: "App Store Connect", status: "Transferred" },
						play: { name: "Play Console", status: "Transferred" },
						key: { name: "Android upload key", status: "Reset" },
						firebase: { name: "Firebase", status: "Recovered" },
					},
				},
				diagnosis: {
					file: "package.json",
					title: "Native dependencies",
					summary: "3 replaced, 1 kept · app code unchanged",
					rows: {
						a: { from: "react-native-code-push", to: "EAS Update" },
						b: { from: "react-native-camera", to: "expo-camera" },
						c: { from: "react-native-splash-screen", to: "expo-splash-screen" },
						d: { from: "in-house-payment-module", to: "Native kept" },
					},
				},
				compliance: {
					file: "app.config.ts",
					title: "Store requirements",
					compliant: "Ready to publish",
					rows: {
						android: {
							label: "Google Play · target API",
							from: "34",
							to: "36",
						},
						ios: { label: "App Store · iOS SDK", from: "17", to: "26" },
						pages: { label: "16 KB memory pages", from: "no", to: "yes" },
						arch: { label: "New Architecture", from: "no", to: "yes" },
					},
				},
				release: {
					appName: "Your app",
					version: "Version 3.0.0",
					update: "Update",
					whatsNew: "What’s new",
					notes: "Support for the latest devices, fixes and improvements.",
					published: "Live on the App Store and Google Play",
					builds: {
						ios: "iOS build · EAS",
						android: "Android build · EAS",
						submit: "Store submission",
					},
				},
			},
		},
	},
};
